// components/auth/SocialAuthButtons.jsx
// Social Authentication Buttons
// ============================================
// Reusable component for social sign-in options.
// Currently supports Google, expandable for Apple/Facebook.
// ============================================

import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

// ============================================
// SOCIAL BUTTON CONFIGS
// ============================================

const SOCIAL_PROVIDERS = {
  google: {
    name: 'Google',
    bgColor: 'bg-white',
    hoverBgColor: 'hover:bg-gray-50',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-300',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
    ),
  },
  apple: {
    name: 'Apple',
    bgColor: 'bg-black',
    hoverBgColor: 'hover:bg-gray-900',
    textColor: 'text-white',
    borderColor: 'border-black',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
      </svg>
    ),
    comingSoon: true,
  },
  facebook: {
    name: 'Facebook',
    bgColor: 'bg-[#1877F2]',
    hoverBgColor: 'hover:bg-[#166FE5]',
    textColor: 'text-white',
    borderColor: 'border-[#1877F2]',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    comingSoon: true,
  },
};

// ============================================
// SOCIAL AUTH BUTTONS COMPONENT
// ============================================

const SocialAuthButtons = ({ 
  mode = 'signin', // 'signin' or 'signup'
  onSuccess,
  onError,
  disabled = false,
  showDivider = true,
  providers = ['google'], // Which providers to show
  compact = false // Compact mode for smaller spaces
}) => {
  const { signInWithGoogle } = useAuth();
  const [loadingProvider, setLoadingProvider] = useState(null);
  
  // Handle provider sign-in
  const handleProviderSignIn = async (provider) => {
    if (disabled || loadingProvider) return;
    
    const config = SOCIAL_PROVIDERS[provider];
    if (config.comingSoon) return;
    
    setLoadingProvider(provider);
    
    try {
      let result;
      
      switch (provider) {
        case 'google':
          result = await signInWithGoogle();
          break;
        // Add other providers here when ready
        // case 'apple':
        //   result = await signInWithApple();
        //   break;
        // case 'facebook':
        //   result = await signInWithFacebook();
        //   break;
        default:
          throw new Error(`Unknown provider: ${provider}`);
      }
      
      if (result.success) {
        onSuccess?.(result);
      } else {
        onError?.(result.error);
      }
      
    } catch (err) {
      console.error(`${provider} sign-in error:`, err);
      onError?.(err.message || `Failed to sign in with ${config.name}`);
    } finally {
      setLoadingProvider(null);
    }
  };
  
  const actionText = mode === 'signup' ? 'Sign up' : 'Sign in';
  
  return (
    <div className="space-y-3">
      {/* Social Buttons */}
      <div className={compact ? 'flex gap-2' : 'space-y-3'}>
        {providers.map((provider) => {
          const config = SOCIAL_PROVIDERS[provider];
          if (!config) return null;
          
          const isLoading = loadingProvider === provider;
          const isDisabled = disabled || loadingProvider !== null || config.comingSoon;
          
          return (
            <button
              key={provider}
              onClick={() => handleProviderSignIn(provider)}
              disabled={isDisabled}
              className={`
                ${compact ? 'flex-1' : 'w-full'}
                flex items-center justify-center gap-3
                px-4 py-3 rounded-lg
                border ${config.borderColor}
                ${config.bgColor} ${config.hoverBgColor}
                ${config.textColor}
                font-medium
                transition-all duration-200
                ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                ${isLoading ? 'animate-pulse' : ''}
                ${config.comingSoon ? 'relative' : ''}
              `}
            >
              {/* Icon */}
              {isLoading ? (
                <LoadingSpinner color={config.textColor === 'text-white' ? 'white' : 'gray'} />
              ) : (
                config.icon
              )}
              
              {/* Text (hidden in compact mode) */}
              {!compact && (
                <span>
                  {config.comingSoon ? `${config.name} (Coming Soon)` : `${actionText} with ${config.name}`}
                </span>
              )}
              
              {/* Coming Soon Badge */}
              {config.comingSoon && compact && (
                <span className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-900 text-xs px-1 rounded">
                  Soon
                </span>
              )}
            </button>
          );
        })}
      </div>
      
      {/* Divider */}
      {showDivider && (
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-500">or continue with email</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// LOADING SPINNER COMPONENT
// ============================================

const LoadingSpinner = ({ color = 'gray' }) => (
  <svg 
    className={`animate-spin h-5 w-5 ${color === 'white' ? 'text-white' : 'text-gray-600'}`} 
    xmlns="http://www.w3.org/2000/svg" 
    fill="none" 
    viewBox="0 0 24 24"
  >
    <circle 
      className="opacity-25" 
      cx="12" 
      cy="12" 
      r="10" 
      stroke="currentColor" 
      strokeWidth="4"
    />
    <path 
      className="opacity-75" 
      fill="currentColor" 
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

// ============================================
// STANDALONE GOOGLE BUTTON (For Simple Use)
// ============================================

export const GoogleSignInButton = ({ 
  mode = 'signin',
  onSuccess,
  onError,
  disabled = false,
  fullWidth = true,
  children
}) => {
  const { signInWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const handleClick = async () => {
    if (disabled || loading) return;
    
    setLoading(true);
    
    try {
      const result = await signInWithGoogle();
      
      if (result.success) {
        onSuccess?.(result);
      } else {
        onError?.(result.error);
      }
    } catch (err) {
      onError?.(err.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };
  
  const actionText = mode === 'signup' ? 'Sign up' : 'Sign in';
  
  return (
    <button
      onClick={handleClick}
      disabled={disabled || loading}
      className={`
        ${fullWidth ? 'w-full' : ''}
        flex items-center justify-center gap-3
        px-4 py-3 rounded-lg
        border border-gray-300
        bg-white hover:bg-gray-50
        text-gray-700 font-medium
        transition-all duration-200
        ${disabled || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      {loading ? (
        <LoadingSpinner color="gray" />
      ) : (
        SOCIAL_PROVIDERS.google.icon
      )}
      <span>{children || `${actionText} with Google`}</span>
    </button>
  );
};

export default SocialAuthButtons;